import { ecorEnv } from '@../../environments/environment';
import { EcorrUtilsService } from '@/common/utils/ecorr-utils.service';
import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { SessionStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppService } from './app.service';
import { IdleTimeoutDialogData } from './support/dialogs/idle-timeout-dialog/idle-timeout-dialog-data';
import { IdleTimeoutDialogService } from './support/dialogs/services';
import { EcorrEvent } from './surveys/services/event.service';
import { FeaturesService } from './features.service';
import { EcorrHeaderComponent } from './common/components/ecorr-header/ecorr-header.component';
import { EcorrNavComponent } from './common/components/ecorr-nav/ecorr-nav.component';
import { EcorrBannerComponent } from './common/components/ecorr-banner/ecorr-banner.component';
import { EcorrFooterComponent } from './common/components/ecorr-footer/ecorr-footer.component';
import { EcorrSC24HeaderComponent } from './landing/components/ecorr-sc24-header/ecorr-sc24-header.component';
import { EcorrSC24FooterComponent } from './landing/components/ecorr-sc24-footer/ecorr-sc24-footer.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: true,
    imports: [
        CommonModule, 
        RouterOutlet,
        EcorrHeaderComponent,
        EcorrNavComponent,
        EcorrBannerComponent,
        EcorrFooterComponent,
        EcorrSC24HeaderComponent,
        EcorrSC24FooterComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    
    idleState = signal('Not started.');
    timedOut = signal(false);
    lastPing = signal<Date | null>(null);
    idleTimeout$: Subscription;
    env = signal(ecorEnv.env);

    currentPath = signal<String>('');
    ref: any;
    isSC24 = signal(false);

    constructor(
        private idle: Idle, 
        private keepalive: Keepalive,
        private router: Router,
        public dialog: MatDialog,
        private idleTimeoutDialogService: IdleTimeoutDialogService,
        location: Location,
        private sessionStorageService: SessionStorageService,
        public appService: AppService,
        private ecorrUtilsService: EcorrUtilsService,
        private featuresService: FeaturesService
    ) {

        this.ref = document.body;

        idle.setIdle(900);
        idle.setTimeout(300);
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        idle.onIdleEnd.subscribe(() => {
            this.closeIdleTimeoutDialog();
            // console.log('[onIdleEnd] IdleTimeoutDialog is closed!');
            this.idleState.set('No longer idle.');
            // console.log(this.idleState());
            this.reset();
        });

        idle.onTimeout.subscribe(() => {
            this.idleTimeoutDialogService.close();
            this.idleState.set('Timed out!');
            this.timedOut.set(true);
            // console.log(this.idleState());
            this.sessionStorageService.store('timedOut', true);
            this.ecorrUtilsService.logout();
        });

        idle.onIdleStart.subscribe(() => {
            this.idle.clearInterrupts();
            this.idleState.set('You have gone idle!');
            // console.log(this.idleState());
            // Only open dialog if not on public routes
            if (!this.currentPath()?.includes('/portal') && 
                !this.currentPath()?.includes('/login') && 
                !this.currentPath()?.includes('/register') && 
                !this.currentPath()?.includes('/404-not-found') && 
                !this.currentPath()?.includes('/faq')) {
                this.openIdleTimeoutDialog();
            }
        });

        keepalive.interval(15);

        keepalive.onPing.subscribe(() => this.lastPing.set(new Date()));

        // Lets check the path everytime the route changes, stop or start the idle check as appropriate.
        this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
            this.currentPath.set(location.path());
            if (this.currentPath().includes('/portal') ||
                this.currentPath().includes('/login') ||
                this.currentPath().includes('/register') ||
                this.currentPath().includes('/404-not-found') ||
                this.currentPath().includes('/faq')) {
                idle.stop();
                this.isSC24.set(false);
            } else if (this.currentPath().includes('/specialcensus')) {
                this.isSC24.set(true);
            } else {
                idle.watch();
                this.isSC24.set(false);
            }
        });
    }

    ngOnInit() {
        /**
         * Clickjack prevention
         * 
         * <style id="antiClickjack">
         *     body {
         *         display: none !important;
         *     }
         * 
         * </style>
         * <script type="text/javascript">
         *     if (self === top) {
         *         var antiClickjack = document.getElementById("antiClickjack");
         *         antiClickjack.parentNode.removeChild(antiClickjack);
         *     } else {
         *         top.location = self.location;
         *     }
         * </script>
         */
        const antiClickjack = document.getElementById('antiClickjack');
        if (antiClickjack) {
            antiClickjack.parentNode?.removeChild(antiClickjack);
        }
    }

    reset() {
        this.idle.watch();
        this.idleState.set('Started.');
        this.timedOut.set(false);
    }

    openIdleTimeoutDialog() {
        const options: IdleTimeoutDialogData = {
            title: 'Warning - Session Expiring',
            content: 'Your session is set to expire in {{countdown  | date: "mm:ss"}} minutes. Please select "Continue" to extend your session or you will be signed out',
            confirmButtonLabel: 'Continue'
        };

        this.idleTimeoutDialogService.open(options);

        this.idleTimeout$ = this.idleTimeoutDialogService.continued().subscribe(res => {
            console.log('[openIdleTimeoutDialog]', 'Received from dialog', res);
        });
    }

    closeIdleTimeoutDialog() {
        this.idleTimeoutDialogService.close();
    }
}
